/**
 * diagnostics — the one diagnostic shape used by the merge engine and, from
 * here on, by every stage of the model compiler (30.03.02 §5.5).
 *
 *   { severity, code, path, layerId, message, detail }
 *
 * Severity is a property of `(code, layerId)`, not of `code` alone. The same
 * `unmatched-where` is a warning from a customer overlay — they authored it
 * against an older base and must keep running — and an error from a country
 * pack, because we wrote both the base and the pack and CI compiles them before
 * a release exists (30.03.02 §5.2.1). It is a lookup rather than an `if`, so a
 * second code can join it without a second mechanism.
 *
 * Nothing here throws. The engine collects every diagnostic and returns them,
 * because a customer needs the whole list, not the first failure.
 */

/** Lower sorts first: errors before warnings before info. */
export const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 }

/** Codes owned by the merge engine and their default severity. */
export const DEFAULT_SEVERITY = {
    'unmatched-where':         'warning',
    'ambiguous-where':         'error',
    'unkeyed-array-op':        'error',
    'reserved-key':            'error',
    'type-mismatch':           'error',
    'layer-conflict':          'error',
    'localization-override':   'warning',
    'unknown-op':              'error',
    'invalid-patch':           'error',
    'array-wholesale-replace': 'warning'
}

/** Per-layer-kind overrides of the default. Only `loc:` layers differ today. */
const SEVERITY_BY_LAYER_KIND = {
    'unmatched-where': { loc: 'error' }
}

/** `loc` for a localization layer, `other` for everything else. */
export const layerKindOf = (layerId) =>
    typeof layerId === 'string' && layerId.startsWith('loc:') ? 'loc' : 'other'

/** The severity a diagnostic `code` has when raised by `layerId`. */
export function severityFor(code, layerId) {
    const byKind = SEVERITY_BY_LAYER_KIND[code]
    const override = byKind?.[layerKindOf(layerId)]
    return override ?? DEFAULT_SEVERITY[code] ?? 'error'
}

/**
 * Build a diagnostic. `severity` may be passed to override the lookup — used
 * for `layer-conflict` downgraded by `allowConflictAt`, and for
 * `array-wholesale-replace` reported as info by `diffToPatch`.
 */
export function diagnostic(code, { path = '', layerId = null, message = '', detail, severity } = {}) {
    const d = {
        severity: severity ?? severityFor(code, layerId),
        code,
        path,
        layerId,
        message
    }
    if (detail !== undefined) d.detail = detail
    return d
}

/** True when any diagnostic is an error — the caller must not publish. */
export const hasErrors = (diagnostics) => diagnostics.some(d => d.severity === 'error')
